import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import TableHeader from './TableHeader';
import TableRow from './TableRow';
import ModalTrackingDetails from "./ModalTrackingDetails";
import ModalEnterData from './ModalEnterData';
import ModalCrewTimesheet from './ModalCrewTimesheet';
import ProductionLoggedModal from './ProductionLoggedModal';
import InventoryTrackingModal from './InventoryTrackingModal';
import UpdateMultipleModal from './UpdateMultipleModal';
import { SchedulingMetricsModal } from './SchedulingMetricsModal';
import axios from 'axios';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { hydrateCrewActivitiesFromRows } from '../utils/crewTimesheetActivitiesCache';
import { forceUnlockAllSheetModals } from '../hooks/useFullscreenSheetModal';
import { useDailyLogFlow } from '../hooks/useDailyLogFlow';
import './ProductionPerformance.css';

function isProductionRateLimit(err) {
  const status = err?.response?.status;
  const message = `${err?.response?.data?.message || ""} ${err?.message || ""}`;
  return status === 429 || status === 503 || /429|rate limit|busy right now/i.test(message);
}

function productionRateLimitMessage() {
  return "Production data is busy right now. Please wait a moment and refresh.";
}

async function getProductionPerformanceWithRetry(url, config) {
  let lastError;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      return await axios.get(url, config);
    } catch (err) {
      lastError = err;
      if (!isProductionRateLimit(err) || attempt === 1) throw err;
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }
  throw lastError;
}

/** Body scroll lock while an excel upload modal is open. */
function usePpUploadModalBodyLock() {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
}

/**
 * Excel upload modal shell:
 * - Desktop / website: compact centered card (original size)
 * - Mobile portrait: content-height card (no empty stretch)
 * - Mobile landscape: near-fullscreen inset (unchanged / preferred)
 * Uses max-lg (1024) so landscape phones (e.g. 844px wide) still get landscape rules.
 */
function PpExcelUploadModalShell({ title, onClose, onDragOver, onDrop, children }) {
  usePpUploadModalBodyLock();

  return createPortal(
    <div
      className="fixed inset-0 z-[10160] bg-black/50 overflow-y-auto max-lg:landscape:overflow-hidden"
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="flex min-h-full items-center justify-center px-4 py-8 max-lg:px-2 max-lg:py-6 max-lg:landscape:block max-lg:landscape:h-[100dvh] max-lg:landscape:min-h-0 max-lg:landscape:p-0">
        <div
          className={[
            'flex w-[820px] max-w-[95vw] max-h-[calc(100dvh-4rem)] flex-col overflow-hidden rounded-xl bg-white shadow-2xl',
            /* Portrait mobile: same L/R inset as landscape (inset-2 = 0.5rem); slightly taller content card */
            'max-lg:w-full max-lg:max-w-none max-lg:max-h-[min(90dvh,640px)]',
            /* Landscape mobile: full inset over app bar + bottom nav */
            'max-lg:landscape:absolute max-lg:landscape:inset-2 max-lg:landscape:h-auto max-lg:landscape:max-h-none max-lg:landscape:w-auto max-lg:landscape:max-w-none',
          ].join(' ')}
        >
          <div className="relative z-10 flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4 max-lg:px-4 max-lg:py-3 shadow-[0_2px_5px_rgba(0,0,0,0.08)]">
            <div className="text-lg max-lg:text-base font-semibold text-gray-800 font-poppins max-lg:landscape:text-[20px] max-lg:landscape:font-bold max-lg:landscape:text-[#2C2C2C]">
              {title}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              title="Close"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* shrink (not grow) so portrait/desktop stay content-sized; landscape flex-1 fills inset */}
          <div className="min-h-0 shrink overflow-y-auto p-5 max-lg:p-3 max-lg:landscape:flex-1 [-webkit-overflow-scrolling:touch]">
            {children}
          </div>

          <div className="flex shrink-0 justify-end gap-3 border-t px-5 py-4 max-lg:px-4 max-lg:py-2.5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 max-lg:px-3 max-lg:py-1.5 text-sm rounded bg-gray-100 hover:bg-gray-200 text-gray-800"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function withSavedTracking(row, update) {
  if (!update) return row;
  const daysParts = update.daysUsed ? String(update.daysUsed).split(" / ") : null;
  return {
    ...row,
    ...(update.laborTracking ? { unitsPlaced: update.laborTracking } : {}),
    ...(update.productionTracking ? { hoursUsed: update.productionTracking } : {}),
    ...(daysParts ? {
      crewDaysUsed: daysParts[0] || row.crewDaysUsed,
      ...(daysParts[1] ? { estCrewDays: daysParts[1] } : {}),
    } : {}),
    originalData: row.originalData
      ? {
          ...row.originalData,
          ...(update.laborTracking ? { laborTracking: update.laborTracking } : {}),
          ...(update.productionTracking ? { productionTracking: update.productionTracking } : {}),
          ...(update.daysUsed ? { daysUsed: update.daysUsed } : {}),
        }
      : row.originalData,
  };
}

function savedTrackingForRow(savedByScope, row) {
  const scopeId = row?.scopeId != null ? String(row.scopeId) : "";
  if (scopeId && savedByScope.has(scopeId)) return savedByScope.get(scopeId);
  const scopeName = row?.scope != null ? String(row.scope) : "";
  if (!scopeName) return null;
  for (const update of savedByScope.values()) {
    if (update.scopeName && String(update.scopeName) === scopeName) return update;
  }
  return null;
}

const TravelAndProduction = ({ metrics: initialMetrics, milesInfo }) => {
  const { authFetch, getAuthConfig, isReady } = useAuthFetch();
  const getAuthConfigRef = useRef(getAuthConfig);
  getAuthConfigRef.current = getAuthConfig;
  const savedTrackingRef = useRef(new Map());
  const projectData = JSON.parse(localStorage.getItem('selectedProjectDetails')) || {};
  const [distances, setDistances] = useState({
    wallerDistance: null,
    kansasCityDistance: null,
    loading: true,
    error: null,
    addressValid: true
  });

  // Add state for metrics to allow updating after modal close
  const [metrics, setMetrics] = useState(initialMetrics || []);

  // Update metrics when props change
  useEffect(() => {
    if (initialMetrics) {
      setMetrics(initialMetrics);
    }
  }, [initialMetrics]);

  // Listen for metrics updates from other components
  useEffect(() => {
    const handleMetricsUpdate = (event) => {
      if (event.detail) {
        setMetrics(event.detail);
      }
    };

    window.addEventListener('metricsUpdated', handleMetricsUpdate);

    return () => {
      window.removeEventListener('metricsUpdated', handleMetricsUpdate);
    };
  }, []);

  // Modal state for scheduling metrics
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    metricType: null,
    currentValues: null,
  });

  // Modal state for Production Tracking
  const [trackingModal, setTrackingModal] = useState({
    isOpen: false,
    rowData: null,
  });

  const [enterDataModal, setEnterDataModal] = useState({
    isOpen: false,
    rowData: null,
  });
  const [enterDataRefreshNonce, setEnterDataRefreshNonce] = useState(0);

  // Crew Timesheet — new PP flow (separate from Enter Data / Labor Tracking)
  const [crewTimesheetModal, setCrewTimesheetModal] = useState({
    isOpen: false,
    rowData: null,
  });

  const [showProductionLogged, setShowProductionLogged] = useState(false);
  const [logProductionContext, setLogProductionContext] = useState(null);
  const [showInventoryTracking, setShowInventoryTracking] = useState(false);
  const [inventoryTrackingContext, setInventoryTrackingContext] = useState(null);
  const dailyLog = useDailyLogFlow();

  // Upload modal (tracking template .xlsx)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Get address components
  const street = projectData.street || '';
  const city = projectData.city || '';
  const state = projectData.state || '';
  const zipCode = projectData.zipCode || '';

  // Add useMemo for address validation
  const addressInfo = useMemo(() => {
    // Use the individual address components if available
    if (street || city || state || zipCode) {
      const firstLine = street;
      const secondLine = [city, state, zipCode].filter(Boolean).join(', ');

      if (firstLine && secondLine) {
        return { text: `${firstLine}, ${secondLine}`, isValid: true };
      } else if (firstLine) {
        return { text: firstLine, isValid: true };
      } else if (secondLine) {
        return { text: secondLine, isValid: true };
      }
    }

    // Fall back to the projectAddress string if individual components aren't available
    if (projectData.projectAddress && projectData.projectAddress !== '-') {
      return { text: projectData.projectAddress, isValid: true };
    }

    return { text: 'Address not available', isValid: false };
  }, [street, city, state, zipCode, projectData.projectAddress]);

  // Add useCallback for the distance calculation
  const calculateDistances = useCallback(async () => {
    try {
      if (!addressInfo.isValid || !isReady) {
        setDistances({
          wallerDistance: null,
          kansasCityDistance: null,
          loading: false,
          error: null,
          addressValid: false
        });
        return;
      }

      setDistances(prev => ({ ...prev, loading: true }));

      // Make API calls for both branches
      const [wallerResponse, kansasCityResponse] = await Promise.all([
        authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/distance/matrix`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origins: ["41229 park 290 Dr, waller, tx 77484"],
            destinations: [addressInfo.text],
            units: "imperial"
          })
        }),
        authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/distance/matrix`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origins: ["2850 fairfax trafficway, Kansas City, KS 66115"],
            destinations: [addressInfo.text],
            units: "imperial"
          })
        })
      ]);

      const [wallerData, kansasCityData] = await Promise.all([
        wallerResponse.json(),
        kansasCityResponse.json()
      ]);

      // Extract distances from responses
      const wallerDistance = wallerData.success && wallerData.data.rows[0]?.elements[0]?.distance?.text;
      const kansasCityDistance = kansasCityData.success && kansasCityData.data.rows[0]?.elements[0]?.distance?.text;

      setDistances({
        wallerDistance: wallerDistance || 'N/A',
        kansasCityDistance: kansasCityDistance || 'N/A',
        loading: false,
        error: null,
        addressValid: true
      });
    } catch (error) {
      console.error('Error calculating distances:', error);
      setDistances({
        wallerDistance: null,
        kansasCityDistance: null,
        loading: false,
        error: 'Failed to calculate distances',
        addressValid: true
      });
    }
  }, [addressInfo, authFetch, isReady]);

  // Update the useEffect to use the memoized values and add debounce
  useEffect(() => {
    // Skip if address is not valid
    if (!addressInfo.isValid) {
      setDistances(prev => ({
        ...prev,
        loading: false,
        addressValid: false
      }));
      return;
    }

    // Debounce the API calls
    const timer = setTimeout(() => {
      calculateDistances();
    }, 1000); // Wait 1 second before making API calls

    return () => clearTimeout(timer);
  }, [addressInfo, calculateDistances]);

  // Function to fetch updated metrics data
  const fetchSchedulingMetrics = async () => {
    if (!projectData?.opportunityNo || !isReady) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/schedules/${projectData.opportunityNo}`,
        await getAuthConfig()
      );

      if (response.data.status === "success" && response.data.data) {
        const data = response.data.data;
        setMetrics([
          {
            label: "Mobs Used/Contracted",
            value: `${data.mobs_used}/${data.mobs_contracted}`,
            type: "mobs",
            used: data.mobs_used,
            total: data.mobs_contracted,
          },
          {
            label: "Days Used/Estimated",
            value: `${data.days_used}/${data.days_estimated}`,
            type: "days",
            used: data.days_used,
            total: data.days_estimated,
          },
          {
            label: "Trips Used/Estimated",
            value: `${data.trip_used}/${data.trip_estimated}`,
            type: "trips",
            used: data.trip_used,
            total: data.trip_estimated,
          },
        ]);
      } else {
        setMetrics([
          { label: "Mobs Used/Contracted", value: "N/A", type: "mobs", used: 0, total: 0 },
          { label: "Days Used/Estimated", value: "N/A", type: "days", used: 0, total: 0 },
          { label: "Trips Used/Estimated", value: "N/A", type: "trips", used: 0, total: 0 },
        ]);
      }
    } catch (error) {
      console.error("Error fetching scheduling metrics:", error);
      setMetrics([
        { label: "Mobs Used/Contracted", value: "N/A", type: "mobs", used: 0, total: 0 },
        { label: "Days Used/Estimated", value: "N/A", type: "days", used: 0, total: 0 },
        { label: "Trips Used/Estimated", value: "N/A", type: "trips", used: 0, total: 0 },
      ]);
    }
  };

  // Handle metric click to open modal
  const handleMetricClick = (metric) => {
    setModalConfig({
      isOpen: true,
      metricType: metric.type,
      currentValues: { used: metric.used, total: metric.total },
    });
  };

  // Handle modal close
  const closeModal = (refreshData = false) => {
    setModalConfig({
      isOpen: false,
      metricType: null,
      currentValues: null,
    });

    // If data was updated in the modal, refresh metrics
    if (refreshData) {
      // Instead of calling local fetch function, trigger global refresh for the parent component to handle
      window.dispatchEvent(new CustomEvent('refreshMetrics'));
    }
  };

  // Open Production Tracking modal (row-specific)
  const openTrackingModal = (row) => {
    setTrackingModal({
      isOpen: true,
      rowData: row,
    });
  };

  // Close Production Tracking modal
  const closeTrackingModal = (refresh = false) => {
    setTrackingModal({
      isOpen: false,
      rowData: null,
    });

    if (refresh) {
      console.log("Save Production Tracking data to API here");
    }
  };

  // Open Enter Data modal (row-specific)
  const openEnterDataModal = (row) => {
    setEnterDataModal({ isOpen: true, rowData: row });
  };
  // Close Enter Data modal
  const closeEnterDataModal = () => {
    setEnterDataModal({ isOpen: false, rowData: null });
  };

  // Crew Timesheet modal — new flow only
  const openCrewTimesheetModal = (row) => {
    setShowProductionLogged(false);
    setShowInventoryTracking(false);
    setLogProductionContext(null);
    setInventoryTrackingContext(null);
    setCrewTimesheetModal({ isOpen: true, rowData: row });
    dailyLog.begin();
  };
  const closeCrewTimesheetModal = () => {
    setCrewTimesheetModal({ isOpen: false, rowData: null });
    dailyLog.close();
  };
  const navigateDailyLog = (step, patch) => {
    dailyLog.navigate(step, {
      ...(patch || {}),
      projectActivities,
    });
  };

  const [showUpdateMultiple, setShowUpdateMultiple] = useState(false);
  const [updateMultipleContext, setUpdateMultipleContext] = useState(null);

  // State for production performance data
  const [tableData, setTableData] = useState([]);
  const [loadingProductionData, setLoadingProductionData] = useState(true);
  const [productionError, setProductionError] = useState(null);

  // Branch addresses with precise coordinates
  const branches = {
    waller: {
      address: "41229 park 290 Dr, waller, tx 77484",
      name: "Waller, TX",
      coordinates: { lat: 30.0687916, lng: -95.9434322 } // Updated precise coordinates for Waller branch
    },
    kansasCity: {
      address: "2850 fairfax trafficway, Kansas City, KS 66115",
      name: "Kansas City, KS",
      coordinates: { lat: 39.1326654, lng: -94.6141754 } // Updated precise coordinates for KC branch
    }
  };

  // Fetch production performance data from API
  useEffect(() => {
    const fetchProductionPerformance = async () => {
      // Get project code (Miter job code) from projectData
      const projectCode = projectData?.projectNo;

      if (!projectCode || !isReady) {
        setLoadingProductionData(false);
        setTableData([]);
        setProductionError(null);
        return;
      }

      try {
        setLoadingProductionData(true);
        setProductionError(null);

        // Use separate API URL for Production Performance (local development)
        const productionPerformanceApiUrl = process.env.REACT_APP_BACKEND_BASEURL;

        const response = await getProductionPerformanceWithRetry(
          `${productionPerformanceApiUrl}/api/miter/production-performance/${projectCode}`,
          await getAuthConfigRef.current()
        );

        // Handle both success and error responses
        if (response.data.status === "error") {
          // API returned error but with 200 status - show empty state
          console.log("Production Performance API returned error:", response.data.message);
          setTableData([]);
          setProductionError(null); // Don't show error, just empty state
          return;
        }

        if (response.data.status === "success" && response.data.data?.rows) {
          // Map API response to table data format
          const mappedData = response.data.data.rows.map((row) => {
            // Parse daysUsed (format: "22" or "22 / 16")
            let crewDaysUsed = "--";
            let estCrewDays = "--";

            if (row.daysUsed && row.daysUsed !== "--") {
              const daysParts = row.daysUsed.split(" / ");
              crewDaysUsed = daysParts[0] || "--";
              estCrewDays = daysParts[1] || "--"; // If no estimated, show "--"
            }

            return {
              scopeId: row.scopeId,
              scope: row.scope || "Unnamed Scope",
              hoursUsed: row.productionTracking || "--", // Production Tracking
              unitsPlaced: row.laborTracking || "--", // Labor Tracking
              crewSize: row.crewSize !== "--" ? String(row.crewSize) : "--",
              estCrewDays: estCrewDays,
              productionPerDay: row.prodPerDay !== "--" && !isNaN(parseFloat(row.prodPerDay))
                ? Math.round(parseFloat(row.prodPerDay)).toLocaleString()
                : row.prodPerDay || "--",
              crewDaysUsed: crewDaysUsed,
              rentalEquipment: row.rentalEquipment || "--",
              estRentalDays: row.estRentalDays || "--",
              activityDetails: row.activityDetails || null,
              // Keep original data for modals
              originalData: row
            };
          });

          setTableData(mappedData.map((row) => withSavedTracking(row, savedTrackingForRow(savedTrackingRef.current, row))));
          hydrateCrewActivitiesFromRows(projectCode, mappedData);
        } else {
          setTableData([]);
        }
      } catch (err) {
        console.error("Error fetching production performance:", err);
        // Handle 404 or other errors gracefully
        if (err.response?.status === 404) {
          // If 404, it means job not found in Miter - show empty state
          setProductionError(null);
          setTableData([]);
        } else if (isProductionRateLimit(err)) {
          setProductionError(productionRateLimitMessage());
        } else {
          setProductionError(err.response?.data?.message || err.message || "Failed to fetch production performance data");
          setTableData([]);
        }
      } finally {
        setLoadingProductionData(false);
      }
    };

    fetchProductionPerformance();
  }, [projectData?.projectNo, isReady]);

  // Activity list for Crew Timesheet / Enter Data (from loaded PP rows)
  const projectActivities = useMemo(
    () => tableData.map((r) => ({ scopeId: r.scopeId, scope: r.scope })),
    [tableData]
  );

  useEffect(() => {
    const projectCode = projectData?.projectNo;
    if (projectCode && tableData.length > 0) {
      hydrateCrewActivitiesFromRows(projectCode, tableData);
    }
  }, [projectData?.projectNo, tableData]);

  const clearEnterDataFlag = useCallback((scopeId, scopeName) => {
    setTableData((prev) => prev.map((row) => {
      const idMatch = scopeId && String(row.scopeId || "") === String(scopeId);
      const nameMatch = scopeName && String(row.scope || "") === String(scopeName);
      if (!idMatch && !nameMatch) return row;
      const details = row.activityDetails ? { ...row.activityDetails, hasEnterData: false } : row.activityDetails;
      const originalDetails = row.originalData?.activityDetails
        ? { ...row.originalData.activityDetails, hasEnterData: false }
        : row.originalData?.activityDetails;
      return {
        ...row,
        hasEnterData: false,
        activityDetails: details,
        originalData: row.originalData
          ? { ...row.originalData, hasEnterData: false, activityDetails: originalDetails }
          : row.originalData,
      };
    }));
  }, []);

  const applyLaborTrackingUpdate = useCallback((update) => {
    const label = update?.laborTracking;
    const daysUsed = update?.daysUsed;
    if (!label && !daysUsed) return;
    const scopeId = update.scopeId != null ? String(update.scopeId) : "";
    if (scopeId) savedTrackingRef.current.set(scopeId, update);
    setTableData((prev) => prev.map((row) => withSavedTracking(row, savedTrackingForRow(savedTrackingRef.current, row))));
  }, []);

  const handleDeleteEnterData = useCallback(async (row) => {
    const projectCode = projectData?.projectNo;
    const scopeName = row?.scope;
    const scopeId = row?.scopeId;
    if (!projectCode || !scopeName) return;
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    const res = await axios.post(
      `${baseUrl}/api/miter/production-performance/${encodeURIComponent(projectCode)}/enter-data/delete`,
      { scopeId, scopeName },
      await getAuthConfigRef.current()
    );
    const payload = res.data?.data || {};
    if (res.data?.status !== "success") {
      throw new Error(res.data?.message || "Failed to delete Enter Data");
    }
    clearEnterDataFlag(payload.scopeId || scopeId, payload.scopeName || scopeName);
    if (payload.laborTracking || payload.daysUsed) {
      applyLaborTrackingUpdate({
        scopeId: payload.scopeId || scopeId,
        scopeName: payload.scopeName || scopeName,
        laborTracking: payload.laborTracking,
        daysUsed: payload.daysUsed,
      });
    }
  }, [projectData?.projectNo, clearEnterDataFlag, applyLaborTrackingUpdate]);

  const applyProductionTrackingUpdate = useCallback((update) => {
    const label = update?.productionTracking;
    if (!label) return;
    const scopeId = update.scopeId != null ? String(update.scopeId) : "";
    if (scopeId) {
      const prev = savedTrackingRef.current.get(scopeId) || {};
      savedTrackingRef.current.set(scopeId, { ...prev, ...update });
    }
    setTableData((prev) => prev.map((row) => withSavedTracking(row, savedTrackingForRow(savedTrackingRef.current, row))));
  }, []);

  useEffect(() => {
    const projectCode = projectData?.projectNo;
    if (!projectCode) return undefined;
    const onLabor = (event) => {
      const detail = event.detail || {};
      if (String(detail.projectCode || "") !== String(projectCode)) return;
      applyLaborTrackingUpdate(detail);
    };
    const onProduction = (event) => {
      const detail = event.detail || {};
      if (String(detail.projectCode || "") !== String(projectCode)) return;
      applyProductionTrackingUpdate(detail);
    };
    window.addEventListener("nettles-labor-saved", onLabor);
    window.addEventListener("nettles-production-saved", onProduction);
    return () => {
      window.removeEventListener("nettles-labor-saved", onLabor);
      window.removeEventListener("nettles-production-saved", onProduction);
    };
  }, [projectData?.projectNo, applyLaborTrackingUpdate, applyProductionTrackingUpdate]);

  const refetchProductionPerformance = useCallback(async (options = {}) => {
    const projectCode = projectData?.projectNo;
    if (!projectCode || !isReady) return;
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    const fresh = options.fresh !== false;
    try {
      const res = await getProductionPerformanceWithRetry(
        `${baseUrl}/api/miter/production-performance/${projectCode}`,
        await getAuthConfigRef.current(fresh ? { params: { fresh: "1" } } : undefined)
      );
      if (res.data.status === 'success' && res.data.data?.rows) {
        const mappedData = res.data.data.rows.map((row) => {
          let crewDaysUsed = '--', estCrewDays = '--';
          if (row.daysUsed && row.daysUsed !== '--') {
            const parts = row.daysUsed.split(' / ');
            crewDaysUsed = parts[0] || '--';
            estCrewDays = parts[1] || '--';
          }
          return {
            scopeId: row.scopeId,
            scope: row.scope || 'Unnamed Scope',
            hoursUsed: row.productionTracking || '--',
            unitsPlaced: row.laborTracking || '--',
            crewSize: row.crewSize !== '--' ? String(row.crewSize) : '--',
            estCrewDays,
            productionPerDay: row.prodPerDay !== '--' && !isNaN(parseFloat(row.prodPerDay))
              ? Math.round(parseFloat(row.prodPerDay)).toLocaleString()
              : row.prodPerDay || '--',
            crewDaysUsed,
            rentalEquipment: row.rentalEquipment || '--',
            estRentalDays: row.estRentalDays || '--',
            activityDetails: row.activityDetails || null,
            originalData: row
          };
        }).map((row) => withSavedTracking(row, savedTrackingForRow(savedTrackingRef.current, row)));
        setTableData(mappedData);
        hydrateCrewActivitiesFromRows(projectCode, mappedData);
      }
    } catch {
      // ignore refresh errors
    }
  }, [projectData?.projectNo, isReady]);

  // Add handleMapClick function similar to ProjectInfo.jsx
  const handleMapClick = () => {
    if (addressInfo.isValid) {
      window.open(`https://maps.google.com/?q=${encodeURIComponent(addressInfo.text)}`, '_blank');
    }
  };

  return (
    <section className="travel-production-section flex flex-col min-w-0 px-8 max-md:px-4 py-4 max-md:py-3 mt-4 max-md:mt-2 mx-auto rounded-xl bg-white max-md:bg-stone-50 max-w-full">
      {/* Modal component */}
      {modalConfig.isOpen && (
        <SchedulingMetricsModal
          isOpen={modalConfig.isOpen}
          onClose={closeModal}
          metricType={modalConfig.metricType}
          currentValues={modalConfig.currentValues}
          projectData={projectData}
        />
      )}

      {/* Production Tracking Modal */}
      {trackingModal.isOpen && (
        <ModalTrackingDetails
          isOpen={trackingModal.isOpen}
          onClose={closeTrackingModal}
          rowData={trackingModal.rowData}
          projectCode={projectData?.projectNo}
        />
      )}

      {enterDataModal.isOpen && (
        <ModalEnterData
          isOpen={enterDataModal.isOpen}
          rowData={enterDataModal.rowData}
          projectCode={projectData?.projectNo}
          projectName={projectData?.projectName}
          projectActivities={projectActivities}
          onClose={closeEnterDataModal}
          refreshNonce={enterDataRefreshNonce}
          onLogProduction={(context) => {
            setLogProductionContext({
              ...(context || {}),
              projectActivities,
            });
            closeEnterDataModal();
            setShowProductionLogged(true);
          }}
          onOpenUpdateMultiple={(context) => {
            setUpdateMultipleContext(context || null);
            setShowUpdateMultiple(true);
          }}
        />
      )}

      {crewTimesheetModal.isOpen && (
        <ModalCrewTimesheet
          isOpen={crewTimesheetModal.isOpen}
          rowData={crewTimesheetModal.rowData}
          projectCode={projectData?.projectNo}
          projectName={projectData?.projectName}
          projectActivities={projectActivities}
          onClose={closeCrewTimesheetModal}
          dailyLogStep={dailyLog.step}
          flowWorkDate={dailyLog.context?.workDate || ''}
          onFlowNavigate={navigateDailyLog}
          onLaborSaved={(update) => {
            applyLaborTrackingUpdate(update);
          }}
          onLogProduction={(context) => {
            navigateDailyLog('production', context);
          }}
        />
      )}

      {(showProductionLogged || dailyLog.productionMounted) && (
        <ProductionLoggedModal
          isOpen={showProductionLogged || dailyLog.productionVisible}
          preserveWhenHidden={!showProductionLogged && dailyLog.productionMounted}
          initialContext={
            showProductionLogged && !dailyLog.crewFlowActive
              ? logProductionContext
              : dailyLog.context
          }
          onClose={() => {
            if (showProductionLogged && !dailyLog.crewFlowActive) {
              setShowProductionLogged(false);
              setLogProductionContext(null);
              return;
            }
            closeCrewTimesheetModal();
          }}
          onFlowNavigate={dailyLog.crewFlowActive ? navigateDailyLog : undefined}
          onProductionSaved={(update) => {
            applyProductionTrackingUpdate(update);
            refetchProductionPerformance({ fresh: false, overlay: update });
          }}
          onLogInventory={(context) => {
            if (dailyLog.crewFlowActive) {
              navigateDailyLog('inventory', context);
              return;
            }
            setInventoryTrackingContext(context || null);
            setShowProductionLogged(false);
            setLogProductionContext(null);
            setShowInventoryTracking(true);
          }}
        />
      )}

      {(showInventoryTracking || dailyLog.inventoryMounted) && (
        <InventoryTrackingModal
          isOpen={showInventoryTracking || dailyLog.inventoryVisible}
          preserveWhenHidden={!showInventoryTracking && dailyLog.inventoryMounted}
          initialContext={
            showInventoryTracking && !dailyLog.crewFlowActive
              ? inventoryTrackingContext
              : dailyLog.context
          }
          onFlowNavigate={dailyLog.crewFlowActive ? navigateDailyLog : undefined}
          onClose={() => {
            if (showInventoryTracking && !dailyLog.crewFlowActive) {
              setShowInventoryTracking(false);
              setInventoryTrackingContext(null);
              forceUnlockAllSheetModals();
              return;
            }
            closeCrewTimesheetModal();
          }}
        />
      )}

      {showUpdateMultiple && (
        <UpdateMultipleModal
          initialContext={updateMultipleContext}
          onClose={() => {
            setShowUpdateMultiple(false);
            setUpdateMultipleContext(null);
          }}
          onUpdateHours={() => {
            setShowUpdateMultiple(false);
            setUpdateMultipleContext(null);
            // Force LaborTracking modal to re-fetch latest data after updating hours.
            setEnterDataRefreshNonce((prev) => prev + 1);
          }}
        />
      )}

      {/* Heading and Project Address - stack on mobile */}
      <div className="travel-production-header flex max-md:flex-col justify-between max-md:justify-start items-center max-md:items-start mb-4 max-md:mb-3 w-full gap-2">
        <h2 className="travel-production-title text-xl max-md:text-lg font-semibold text-zinc-800">Travel & Production</h2>
        <div className="travel-production-address text-sm max-md:text-xs text-gray-700 font-medium max-md:w-full">
          Project Address: {' '}
          {addressInfo.isValid ? (
            <button
              onClick={handleMapClick}
              className="text-gray-900 hover:text-red-700 hover:underline transition-colors"
            >
              {addressInfo.text}
            </button>
          ) : (
            <span className="text-red-600 font-medium">{addressInfo.text}</span>
          )}
        </div>
      </div>

      {/* Travel Heading and Miles Info - stack on mobile */}
      <div className="travel-miles-row flex max-md:flex-col justify-between max-md:justify-start items-center max-md:items-start mb-2 max-md:mb-3 w-full gap-2">
        <div className="travel-subtitle text-lg max-md:text-base font-semibold text-gray-700">Travel</div>
        {/* Miles info bar */}
        {distances.loading ? (
          <div className="travel-miles-text text-sm max-md:text-xs text-gray-700">Calculating distances...</div>
        ) : !distances.addressValid ? (
          <div className="travel-miles-text text-sm max-md:text-xs text-gray-700 font-medium max-md:w-full">
            Miles to project site: <span className="text-red-600 font-medium">Cannot calculate - address required</span>
          </div>
        ) : distances.error ? (
          <div className="travel-miles-text text-sm max-md:text-xs text-gray-700 font-medium max-md:w-full">
            Miles to project site: <span className="text-orange-600 font-medium">{distances.error}</span>
          </div>
        ) : (
          <div className="travel-miles-text text-sm max-md:text-xs text-gray-700 font-medium max-md:w-full">
            Miles to project site from: &nbsp;
            <span>{branches.kansasCity.name}: <b>{distances.kansasCityDistance}</b></span>
            &nbsp;&nbsp;
            <span>{branches.waller.name}: <b>{distances.wallerDistance}</b></span>
          </div>
        )}
      </div>
      {/* Metrics Row - stack vertically on mobile; PWA keeps 3-cell row */}
      <div className="travel-metrics-row flex flex-row max-md:flex-col gap-4 max-md:gap-3 mb-6 max-md:mb-4 w-full">
        {metrics && metrics.map((metric, idx) => (
          <div
            key={idx}
            className="travel-metric-card flex-1 max-md:w-full rounded-lg shadow-sm bg-[#f3f4f6] cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => handleMetricClick(metric)}
          >
            <div className="travel-metric-header rounded-t-lg py-2 max-md:py-1.5 text-base max-md:text-sm font-semibold text-white text-center" style={{ background: '#175c63' }}>
              {metric.label}
            </div>
            <div
              className="travel-metric-value py-4 max-md:py-3 text-2xl max-md:text-xl font-bold text-center"
              style={{ color: metric.used <= metric.total ? '#34d399' : '#b91c1c' }}
            >
              {metric.value}
            </div>
          </div>
        ))}
      </div>
      {/* Production Performance heading and upload actions — stack on mobile */}
      <div className="flex max-md:flex-col justify-between max-md:justify-start items-center max-md:items-start mb-2 w-full gap-2 max-md:gap-1.5">
        <div className="text-lg max-md:text-base font-semibold text-gray-700 shrink-0">Production Performance</div>
        <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:w-full max-md:gap-1 min-w-0 flex-wrap justify-end max-md:justify-start">
          <div className="flex items-center gap-2 max-md:w-full max-md:justify-between max-md:py-1 max-md:px-2 max-md:rounded max-md:bg-gray-50 max-md:border max-md:border-gray-200 min-w-0">
            <span className="text-sm max-md:text-xs text-gray-700 font-medium md:truncate">Upload Excel sheet data</span>
            <button
              type="button"
              onClick={() => setIsUploadModalOpen(true)}
              className="p-1 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors shrink-0"
              title="Open Tracking Template Files"
            >
              <svg className="w-4 h-4" viewBox="0 0 60 60" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 18V2C0 2 0 0 2 0C4 0 4 2 4 2V18C4 21.3137 6.68628 24 10 24C13.3137 24 16 21.3137 16 18V6C16 4.89544 15.1046 4 14 4C12.8954 4 12 4.89544 12 6V20C12 20 12 22 10 22C8 22 8 20 8 20V6C8 2.68629 10.6863 0 14 0C17.3137 0 20 2.68629 20 6V18C20 23.5228 15.5228 28 10 28C4.47716 28 0 23.5228 0 18Z" />
                <path fillRule="evenodd" clipRule="evenodd" d="M50 0H24V18C24 25.732 17.732 32 10 32H4V54C4 57.3136 6.68628 60 10 60H50C53.3136 60 56 57.3136 56 54V6C56 2.68629 53.3136 0 50 0ZM44 16H28V20H44V16ZM44 28H28V32H44V28ZM16 40H44V44H16V40Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      {/* Production Performance Table — overflow-x when content min-widths exceed viewport (zoom, sidebar, etc.) */}
      <div className="w-full min-w-0 overflow-x-auto text-lg max-md:text-base text-center text-zinc-800 overscroll-x-contain">
        <div className="min-w-max">
          <TableHeader />

          {loadingProductionData ? (
            // Show loading state - keep table structure visible
            <div className="flex items-center justify-center py-8 bg-white">
              <div className="text-gray-600 text-sm">Loading production performance data...</div>
            </div>
          ) : productionError ? (
            // Show error state
            <div className="flex items-center justify-center py-8 bg-white">
              <div className="text-red-600 text-sm">
                Error: {productionError}
              </div>
            </div>
          ) : tableData.length === 0 ? (
            // Show empty state
            <div className="flex items-center justify-center py-8 bg-white">
              <div className="text-gray-600 text-sm">
                {projectData?.projectNo
                  ? "No production performance data available for this project."
                  : "Please select a project to view production performance data."}
              </div>
            </div>
          ) : (
            // Show table data
            tableData.map((row, index) => (
              <TableRow
                key={row.scopeId || index}
                data={row}
                isEven={index % 2 === 0}
                onTrackingClick={openTrackingModal}
                onEnterDataClick={openEnterDataModal}
                onCrewTimesheetClick={openCrewTimesheetModal}
                onDeleteEnterData={handleDeleteEnterData}
              />
            ))
          )}
        </div>
      </div>

      {/* Production Performance – Tracking Template Upload Modal (same design as Project Database upload modal) */}
      {isUploadModalOpen && (
        <ProductionPerformanceUploadModal
          projectNo={projectData?.projectNo}
          onClose={() => setIsUploadModalOpen(false)}
          onUploadSuccess={refetchProductionPerformance}
        />
      )}
    </section>
  );
};

/** Modal for viewing/uploading tracking template (.xlsx) – same UI as Project Database > Insurance & Bonds modal. */
function ProductionPerformanceUploadModal({ projectNo, onClose, onUploadSuccess }) {
  const { getAuthConfig } = useAuthFetch();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const fileInputRef = React.useRef(null);

  const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        const base64 = result && result.indexOf('base64,') >= 0 ? result.split('base64,')[1] : result;
        resolve(base64 || '');
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const uploadFile = async (file) => {
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) return;
    if (!projectNo) {
      setUploadError('Select a project first.');
      return;
    }
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');
    try {
      const fileBase64 = await readFileAsBase64(file);
      const response = await axios.post(
        `${baseUrl}/api/miter/production-performance/${projectNo}/upload`,
        { fileBase64 },
        await getAuthConfig({ headers: { 'Content-Type': 'application/json' } })
      );
      if (response.data.status === 'success') {
        setUploadSuccess(response.data.message || `Uploaded ${response.data.data?.rowsUploaded ?? 0} rows.`);
        setFiles((prev) => [...prev, { name: file.name, modified: new Date().toLocaleString() }]);
        if (typeof onUploadSuccess === 'function') onUploadSuccess();
      } else {
        setUploadError(response.data.message || 'Upload failed.');
      }
    } catch (err) {
      setUploadError(err.response?.data?.message || err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fileList = e.dataTransfer?.files;
    if (fileList?.length) {
      const file = Array.from(fileList).find((f) => f.name.toLowerCase().endsWith('.xlsx') || f.name.toLowerCase().endsWith('.xls'));
      if (file) uploadFile(file);
    }
  };

  const onFileSelect = (e) => {
    const fileList = e.target?.files;
    if (fileList?.length) {
      const file = Array.from(fileList).find((f) => f.name.toLowerCase().endsWith('.xlsx') || f.name.toLowerCase().endsWith('.xls'));
      if (file) uploadFile(file);
    }
    e.target.value = '';
  };

  const titleCrumb = `Upload Production Performance Data`;

  return (
    <PpExcelUploadModalShell
      title={titleCrumb}
      onClose={onClose}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
          {/* <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-600">
              Files inside <span className="font-medium">{contextPath}</span>
            </div>
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              className="inline-flex items-center gap-2 text-sm text-red-700 hover:underline"
              title="Open in SharePoint 123"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 3h7m0 0v7m0-7L10 14M5 10v10a1 1 0 001 1h10" />
              </svg>
              Open in SharePoint
            </a>
          </div> */}

          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 flex">
              <div className="w-2/3">Name</div>
              <div className="w-1/3">Modified</div>
            </div>
            <div className="max-h-64 max-md:max-h-40 overflow-auto divide-y">
              {uploadError && (
                <div className="px-4 py-2 text-sm text-red-600 bg-red-50">{uploadError}</div>
              )}
              {uploadSuccess && (
                <div className="px-4 py-2 text-sm text-green-700 bg-green-50">{uploadSuccess}</div>
              )}
              {files.length === 0 && !uploadSuccess ? (
                <div className="px-4 py-6 text-sm text-gray-500">No files yet.</div>
              ) : (
                files.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="px-4 py-3 text-sm flex">
                    <div className="w-2/3 truncate">
                      <span className="text-red-700">{f.name}</span>
                    </div>
                    <div className="w-1/3 text-gray-600">{f.modified}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div
            className="mt-6 max-md:mt-3 h-40 max-md:h-32 rounded-md border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500 max-md:text-sm"
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
            }}
            onDrop={onDrop}
          >
            <div className="mb-2">Drag and drop files here</div>
            <div className="text-xs mb-3">or</div>
            <button
              type="button"
              className="px-3 py-1.5 rounded bg-red-700 text-white text-sm hover:bg-red-800 disabled:opacity-60"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : 'Choose files'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".xlsx,.xls"
              className="hidden"
              onChange={onFileSelect}
            />
          </div>
    </PpExcelUploadModalShell>
  );
}

export default TravelAndProduction;
